/**
 * PreviewListControlGroup — segmented button group for preview list controls.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { cn } from '@/lib/utils';

export interface PreviewListControlGroupProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}

function PreviewListControlGroupInner<T extends string>({
  label,
  options,
  value,
  onChange,
}: PreviewListControlGroupProps<T>) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={value === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded border px-1.5 py-0.5 text-[10px] tabular-nums transition-colors',
            value === option.id
              ? 'border-primary/40 bg-primary/10 text-foreground'
              : 'border-border/60 bg-background text-muted-foreground hover:bg-muted/40',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const PreviewListControlGroup = PreviewListControlGroupInner as <T extends string>(
  props: PreviewListControlGroupProps<T>,
) => ReturnType<typeof PreviewListControlGroupInner>;

export default PreviewListControlGroup;
